import { useMemo } from 'react';
import { infiniteQueryOptions, skipToken, useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import { isEmptyString } from '@/utils/string';
import { isRecord } from '@/utils/objects';
import { campaignKeys } from '../campaign/entity';
import { getCampaigns, useCampaignOptional } from '../campaign/queries';
import { useEnvironmentOptional } from '../environment/queries';
import {
  activityLogKeys,
  type ActivityLog,
  type ActivityLogCustomer,
  type ActivityLogFilters,
  type ActivityLogObject,
  type ActivityLogRow,
  type ActivityLogSegment,
  type ActivityLogTab,
} from './entity';

/** Identified-customer logs are capped at 50 per page server-side; match it for both tabs. */
const PAGE_SIZE = 50;

/** Auto-refresh cadence (10s). */
export const ACTIVITY_LOGS_REFRESH_INTERVAL_MS = 10_000;

// The generated client types the response as a union (JSON body | raw Blob/Response/…); narrow to
// the JSON page shape we actually consume by the presence of `logs`.
type LogsPage = Extract<Awaited<ReturnType<typeof hydraUiApi.logs.list>>, { logs: unknown }>;

function blankToUndefined(value: string): string | undefined {
  return isEmptyString(value) ? undefined : value;
}

/** Key a customer by both its external and internal id (a log may reference either). */
function indexCustomer(customerById: Map<string, ActivityLogCustomer>, customer: ActivityLogCustomer) {
  if (!isEmptyString(customer.id)) customerById.set(customer.id, customer);
  if (!isEmptyString(customer.internal_id)) customerById.set(customer.internal_id, customer);
}

/**
 * Build id → record lookups from every page's sideloaded relations. The generated type marks
 * `customers` non-optional, but the anonymous endpoint returns `null` for it at runtime — the
 * `?? []` guards against that spec/runtime mismatch.
 */
function buildRelationLookups(pages: LogsPage[]) {
  const customerById = new Map<string, ActivityLogCustomer>();
  const objectById = new Map<string, ActivityLogObject>();
  const segmentById = new Map<number, ActivityLogSegment>();
  for (const page of pages) {
    for (const customer of page.customers ?? []) indexCustomer(customerById, customer);
    for (const object of page.objects ?? []) objectById.set(object.id, object);
    for (const segment of page.segments ?? []) segmentById.set(segment.id, segment);
  }
  return { customerById, objectById, segmentById };
}

/** segment-build logs carry the segment id in `name`. */
function segmentBuildSegment(
  log: ActivityLog,
  segmentById: Map<number, ActivityLogSegment>,
): ActivityLogSegment | undefined {
  const id = Number(log.name);
  return Number.isNaN(id) ? undefined : segmentById.get(id);
}

/** membership-change logs reference a segment via `attrs.entered`/`attrs.exited` (only when exactly one). */
function membershipChangeSegment(
  log: ActivityLog,
  segmentById: Map<number, ActivityLogSegment>,
): ActivityLogSegment | undefined {
  if (!isRecord(log.attrs)) return undefined;
  const record = log.attrs;
  const entered = Array.isArray(record.entered) ? record.entered : [];
  const exited = Array.isArray(record.exited) ? record.exited : [];
  const ids = [...entered, ...exited];
  return ids.length === 1 ? segmentById.get(Number(ids[0])) : undefined;
}

function resolveLogSegment(
  log: ActivityLog,
  segmentById: Map<number, ActivityLogSegment>,
): ActivityLogSegment | undefined {
  if (log.type === 'segment_build') return segmentBuildSegment(log, segmentById);
  if (log.type === 'profile_segment_membership_change') return membershipChangeSegment(log, segmentById);
  return undefined;
}

/** Join each page's logs to their sideloaded customer/object/segment records. */
function buildLogRows(pages: LogsPage[], lookups: ReturnType<typeof buildRelationLookups>): ActivityLogRow[] {
  const { customerById, objectById, segmentById } = lookups;
  return pages.flatMap((page) =>
    (page.logs ?? []).map((log) => ({
      log,
      customer: customerById.get(log.customer_id ?? log.internal_id ?? ''),
      object: isEmptyString(log.object_id ?? '') ? undefined : objectById.get(log.object_id ?? ''),
      segment: resolveLogSegment(log, segmentById),
    })),
  );
}

export interface UseLogsInput {
  /**
   * Numeric workspace id (== the API `environment_id`) as a string. Resolve the `last` route
   * alias before calling — the list cache key derives a number from this, and a non-numeric id
   * would key outside the `['workspace', <id>]` subtree that workspace-switch eviction clears.
   * Non-numeric input disables the query.
   */
  environmentId: string;
  tab: ActivityLogTab;
  filters: ActivityLogFilters;
  /** When true, poll every 10s (the page owns the toggle + `save_auto_refresh` user flag). */
  autoRefresh: boolean;
}

/**
 * Infinite (cursor-paginated) options for the logs feed. `services/db` reads are usually the
 * `getX`/`useX` (`useQuery`) trio, but the logs feed accumulates pages ("load more") and
 * auto-refresh re-fetches all loaded pages, so it uses `useInfiniteQuery` — the sanctioned shape for
 * high-volume paginated feeds. Keyed under the workspace scope for clean teardown.
 *
 * @public
 */
export const logsInfiniteQueryOptions = ({ environmentId, tab, filters, autoRefresh }: UseLogsInput) => {
  const isAnonymous = tab === 'anonymous';
  const numericEnvironmentId = Number(environmentId);
  return infiniteQueryOptions({
    queryKey: activityLogKeys.list(numericEnvironmentId, { tab, filters }),
    initialPageParam: '',
    queryFn: ({ pageParam, signal }): Promise<LogsPage> =>
      hydraUiApi.logs.list({
        params: {
          path: { environment_id: environmentId },
          query: {
            anonymous: isAnonymous ? true : undefined,
            type: blankToUndefined(filters.type),
            name: blankToUndefined(filters.name),
            from: blankToUndefined(filters.from),
            to: blankToUndefined(filters.to),
            limit: PAGE_SIZE,
            continuation: isEmptyString(pageParam) ? undefined : pageParam,
          },
        },
        signal,
      }),
    getNextPageParam: (lastPage: LogsPage) =>
      isEmptyString(lastPage.meta.continuation) ? undefined : lastPage.meta.continuation,
    refetchInterval: autoRefresh ? ACTIVITY_LOGS_REFRESH_INTERVAL_MS : false,
    // A non-numeric id (an unresolved `last` alias) must not fetch: it would cache under
    // `['workspace', NaN, …]`, which no workspace-switch eviction ever clears.
    enabled: Number.isInteger(numericEnvironmentId) && numericEnvironmentId > 0,
  });
};

/**
 * Activity-logs feed over `GET /v1/environments/{id}/logs`. Cursor pagination via
 * `meta.continuation` ("load more"); the reply sideloads `customers` / `objects` / `segments`,
 * which are joined by id into row view-models so the cells never fetch per-row.
 *
 * @public
 */
export function useLogs(input: UseLogsInput) {
  const query = useInfiniteQuery(logsInfiniteQueryOptions(input));

  const { rows, segmentById } = useMemo(() => {
    const pages = query.data?.pages ?? [];
    const lookups = buildRelationLookups(pages);
    // `segmentById` is also returned so the expanded-row JSON formatter can resolve the segment ids
    // in `entered`/`exited` arrays to name links.
    return { rows: buildLogRows(pages, lookups), segmentById: lookups.segmentById };
  }, [query.data]);

  return {
    rows,
    segmentById,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    hasMore: query.hasNextPage,
    fetchMore: query.fetchNextPage,
    isFetchingMore: query.isFetchingNextPage,
    refetch: query.refetch,
    /** ms epoch of the last successful fetch — feeds the "Last updated …" label. */
    lastUpdated: query.dataUpdatedAt,
  };
}

/**
 * Workspace `sent_events` / `sent_attributes` (the Activity Type name filter's options), the
 * `identifiers` config (`{ email, id, cio_id, … }` → numeric state; `0` = disabled) used to count
 * how many changed attributes are identifiers in the Activity Name, and whether the custom SMS
 * action is enabled (drives the twilio/sms Activity Type swap).
 *
 * Built on the shared `environment` entity: workspace-scoped cache with `last`-alias handling.
 *
 * @public
 */
export function useActivityLogEnvironment(environmentId: string) {
  const query = useEnvironmentOptional(Number(environmentId));
  const env = query.data?.environment;
  return {
    sentEvents: env?.sent_events ?? [],
    sentAttributes: env?.sent_attributes ?? [],
    // `identifiers` is loosely typed on the wire; narrowed to the state map the identifier count needs.
    identifiers: (env?.identifiers ?? {}) as Record<string, number>,
    customSmsEnabled: env?.enabled_actions?.includes('sms') ?? false,
  };
}

/** A webhook campaign for the Activity Log name filter — `id` is the value sent as the `name` filter. */
export interface WebhookCampaignOption {
  id: string;
  name: string;
}

/**
 * Webhook-triggered campaigns for the Activity Log's webhook name filter, searched server-side. For
 * `webhook_event` the logs API's `name` filter matches the campaign id (not a free-text name), so
 * this picker resolves a campaign name → its id. The campaigns list pages at 20 with no size
 * override, so the whole set can't be loaded for a client-side filter — instead each search term
 * (debounced by the caller) refetches with `search`.
 *
 * Built on the `campaign` entity's list read (non-suspense, per the `useXOptional` pattern): each
 * (workspace, search) page caches under `campaignKeys.list` and cluster-populates the campaign
 * detail slots it returns.
 *
 * @public
 */
export function useWebhookCampaigns(environmentId: string, search: string) {
  const workspaceId = Number(environmentId);
  const qc = useQueryClient();
  const active = Number.isInteger(workspaceId) && workspaceId > 0;
  const params = { page: 1, type: 'webhook', search: blankToUndefined(search) };
  const query = useQuery({
    queryKey: active ? campaignKeys.list(workspaceId, params) : ['campaign', 'list', 'inactive'],
    queryFn: active ? () => getCampaigns(workspaceId, params, qc) : skipToken,
    staleTime: 60_000,
  });
  const campaigns: WebhookCampaignOption[] = (query.data?.campaigns ?? []).map((campaign) => ({
    id: String(campaign.id),
    name: campaign.name,
  }));
  return { campaigns, isLoading: query.isLoading };
}

/**
 * Resolve a single campaign's name by id — seeds the webhook name filter's input when the filter is
 * populated from a deep link (`?type=webhook_event&name=<campaignId>`), where only the id is known.
 * Reads the `campaign` entity's by-id slot, so a campaign the cluster already loaded resolves
 * without a request.
 *
 * @public
 */
export function useCampaignName(environmentId: string, campaignId: string): string | undefined {
  const workspaceId = Number(environmentId);
  const query = useCampaignOptional({
    workspaceId,
    id: campaignId,
    enabled: Number.isInteger(workspaceId) && workspaceId > 0,
  });
  return query.data?.name;
}

/**
 * Server-side event-name search for the Activity Log's event name filter. The event_names endpoint
 * filters names by `search` in the database, so any event in the workspace is findable — unlike the
 * bounded `sent_events` list the filter used to fall back to, which silently hid events outside it
 * (AUD-4186). Only fires while there is a (debounced) search term; the empty state is served from the
 * cached `sent_events` list at the call site, so mounting the filter costs no request. Capped at one
 * page (the endpoint's max size); a term specific enough to matter narrows well under that.
 *
 * @public
 */
export function useEventNames(environmentId: string, search: string) {
  const workspaceId = Number(environmentId);
  const term = search.trim();
  const active = Number.isInteger(workspaceId) && workspaceId > 0 && !isEmptyString(term);
  const query = useQuery({
    queryKey: active ? activityLogKeys.eventNames(workspaceId, term) : ['activity-log', 'event-names', 'inactive'],
    queryFn: active
      ? async () => {
          const raw = await hydraUiApi.events.getEventNames({
            params: { path: { environment_id: String(workspaceId) }, query: { search: term, page: 1, size: 50 } },
          });
          // Each ListItem serializes its event name as `id` (Go `json:"id"`).
          return (raw.events ?? []).map((event) => event.id);
        }
      : skipToken,
    staleTime: 60_000,
  });
  return { eventNames: query.data ?? [], isLoading: query.isLoading };
}
